import React, { useEffect, useRef, useMemo, useState } from "react";
import { PrismLoading, PrismDialog, PrismButton } from "@prism-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft,
  faExclamationTriangle,
  faWarning,
} from "@fortawesome/free-solid-svg-icons";
import "./Essay.css";
import ApplicationHeader from "../ApplicationHeader";
import SaveAndStepButton from "../SaveAndStepButton.jsx";
import { useDebounce } from "../../../hooks/useDebounce";
import { ESSAY_PROMPT, ESSAY_MAX_WORD_COUNT } from '../constants'
import MessageEditor from "../../MessageEditor/MessageEditor.tsx";
import EssayAiCoach from "./EssayAiCoach";
import axios from "axios";

const EssayStep = ({application, jumpToStep, saveApplication }) => {
  var thisPageStep = 6;

  if (application?.applicant_type_id === 1) {
    thisPageStep = 5;
  }

  const [id, setId] = useState(application.id);
  const [essay, setEssay] = useState(application.essay);
  const [essayPlainText, setEssayPlainText] = useState(""); 
  const [essayWordCount, setEssayWordCount] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const debouncedEssay = useDebounce(essay, 500);
  const [saving, setSaving] = useState(false);
  const [showTooWordy, setShowTooWordy] = useState(false);
  const editorRef = useRef(null); // Ref for MessageEditor

  const wordCount = useMemo(() => {
    const words = essayPlainText.split(/\s+/);
    const count = words.filter((w) => w !== "").length
    setEssayWordCount(count);
    return count;
  }, [essayPlainText]);

  useEffect(() => {
  axios
      .get("/api/applications/my_application")
      .then((response) => {
        let applications = response.data;
        let application = applications.find((a) => a.id === id);  // BUG FIX: Use strict equality
        console.log("in useEffect: ", application);
        setEssay(application.essay);
        htmlToPlainText(application.essay);
      })
      .catch((error) => {
        console.error("Error fetching application:", error);
        // Component continues to render with default/existing essay state
      })
  }, [])

  useEffect(() => {
    let isMounted = true;
    setSaving(true);
    saveApplication({ ...application, essay: essay})
      .finally(() => {
        if(isMounted) {
        setTimeout(() => setSaving(false), 500);
      }
    });

    return () => {
      isMounted = false;
      saveApplication({ ...application, essay: essay}); // Save before unmounting
    };
  }, [debouncedEssay]);

  const onContinue = async () => {
    if (essayWordCount > ESSAY_MAX_WORD_COUNT) {
      setShowTooWordy(true);
      return;
    }
  
    let isMounted = true; // Track if the component is mounted

    await saveApplication({
      ...application,
      essay: essay,
    }).finally(() => {
      if (isMounted) {
        jumpToStep(thisPageStep + 1); // Only navigate if the component is still mounted
      }
    });
  
    return () => {
      isMounted = false; // Cleanup when unmounted
      saveApplication({ ...application, essay: essay}); // Save before unmounting
    };
  };
  
  const onBack = async () => {
    let isMounted = true; // Track if the component is mounted
  
    await saveApplication({
      ...application,
      essay: essay,
    }).finally(() => {
      if (isMounted) {
        jumpToStep(thisPageStep - 1); // Only navigate if the component is still mounted
      }
    });
  
    return () => {
      isMounted = false; // Cleanup when unmounted
      saveApplication({ ...application, essay: essay}); // Save before unmounting
    };
  };

  const htmlToPlainText = (html) => {
    const plainText = editorRef.current?.getText();
    setEssayPlainText(plainText || "");   
  }
  const handleEssayChange = (html) => {
    setEssay(html)
    htmlToPlainText(html)
  };

  const handleFormSubmit = async (event) => {
    if (event) {
      event.preventDefault();
    }
    await saveApplication({ ...application, essay: essay});
  };

  return (
    <div>
      <ApplicationHeader heading="Personal Statement" message={ESSAY_PROMPT} />

      <EssayAiCoach />

      <form onSubmit={handleFormSubmit}>
        <div>
          <MessageEditor
            ref={editorRef}
            defaultValue={essay}
            value={essay} // Pass HTML content to MessageEditor
            onChange={handleEssayChange} // Update both HTML and plain text
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "0.35em",
            }}
          >
            <div>
              {wordCount > ESSAY_MAX_WORD_COUNT ? (
                <FontAwesomeIcon color="var(--color-danger)" icon={faWarning} />
              ) : undefined}
            </div>
            <div style={{
              fontSize: '0.85rem',
              color: 'var(--color-text-faded)',
            }}>
              {wordCount} words
            </div>
            {saving ? <PrismLoading as="spinner" container="inline" /> : undefined}
          </div>
          <div className="nav-btns">
            <SaveAndStepButton
              buttonText="Back"
              icon={faChevronLeft}
              iconPosition="left"
              onSave={handleFormSubmit}
              onSuccessFunction={onBack}
            />
            <SaveAndStepButton
              buttonText="Continue"
              icon={faChevronRight}
              iconPosition="right"
              componentsToCheck={["essay"]}
              onSave={handleFormSubmit}
              onSuccessFunction={onContinue}
            />
          </div>
        </div>
      </form>
      <PrismDialog open={showTooWordy} close-method="user" onPrismClose={() => setShowTooWordy(false)}>
        <span slot="header">Essay Too Long</span>
        <p>
          At {wordCount} words, your essay is too long. Please edit it so that
          it is not longer than {ESSAY_MAX_WORD_COUNT} words.
        </p>
        <div slot="footer">
          <PrismButton tone="primary" onPrismClick={() => setShowTooWordy(false)}>Ok, I understand</PrismButton>
        </div>
      </PrismDialog>
    </div>
  );
};

export default EssayStep;